const VIMEO_SHARE_HOSTS = new Set(['vimeo.com', 'www.vimeo.com'])
const VIMEO_EMBEDDED_HOST = 'player.vimeo.com'
const VIMEO_ALLOWED_HOSTS = new Set([...VIMEO_SHARE_HOSTS, VIMEO_EMBEDDED_HOST])

function isValidVimeoHost(url: string): boolean {
  try {
    const { hostname } = new URL(url)
    return VIMEO_ALLOWED_HOSTS.has(hostname.toLowerCase())
  } catch {
    return false
  }
}

export function isVimeoShareHost(host: string): boolean {
  return VIMEO_SHARE_HOSTS.has(host.toLowerCase())
}

/**
 * Converts any accepted Vimeo URL form to a player.vimeo.com embed src.
 * Returns null if the URL is not a recognised Vimeo video URL.
 *
 * Accepted forms:
 *   https://vimeo.com/{id}                         – public
 *   https://vimeo.com/{id}/{hash}                  – unlisted share URL
 *   https://player.vimeo.com/video/{id}            – public embed URL
 *   https://player.vimeo.com/video/{id}?h={hash}   – unlisted embed URL
 */
export function vimeoUrlToEmbedSrc(url: string): string | null {
  if (!isValidVimeoHost(url)) return null

  const parsed = new URL(url)
  const host = parsed.hostname.toLowerCase()
  const pathParts = parsed.pathname.split('/').filter(Boolean)

  let id: string
  let h: string | null

  if (isVimeoShareHost(host)) {
    // Only accept /{id} or /{id}/{hash} — reject group, album, channel, and other non-video paths.
    if (pathParts.length > 2) return null
    const [first, second] = pathParts
    if (!first || !/^\d+$/.test(first)) return null
    id = first
    const pathHash = second && !/^\d+$/.test(second) ? second : null
    h = pathHash ?? parsed.searchParams.get('h')
  } else {
    // player.vimeo.com: must be /video/{id}
    if (pathParts.length !== 2) return null
    const [prefix, videoId] = pathParts
    if (prefix !== 'video' || !videoId || !/^\d+$/.test(videoId)) return null
    id = videoId
    h = parsed.searchParams.get('h')
  }

  const params = new URLSearchParams()
  if (h) params.set('h', h)
  for (const key of ['badge', 'autopause']) {
    const val = parsed.searchParams.get(key)
    if (val !== null) params.set(key, val)
  }

  const queryString = params.toString()
  const suffix = queryString ? '?' + queryString : ''
  return `https://${VIMEO_EMBEDDED_HOST}/video/${id}${suffix}`
}
